import axios from 'axios';
import * as constants from '../constants/CONSTANT';
import { crmAuthHeaders } from './DataApi';

const baseUrl = `${constants.API_BASE_URL}/api/custom-scripts`;

/**
 * List active custom_script rows (picker), same as Action Manager.
 * @param {string} [targetObject] - e.g. workflow object name
 * @param {{ matchObjectOnly?: boolean }} [options] - default false: universal + object-scoped
 * @returns {Promise<{ success: boolean, data?: array, message?: string }>}
 */
export async function listCustomScriptsForObject(targetObject, options = {}) {
  const params = new URLSearchParams();
  if (targetObject != null && String(targetObject).trim() !== "") {
    params.set("targetObject", String(targetObject).trim());
  }
  if (options.matchObjectOnly) {
    params.set("matchObjectOnly", "true");
  }
  if (Array.isArray(options.scriptTypes) && options.scriptTypes.length > 0) {
    params.set("scriptTypes", options.scriptTypes.map((s) => String(s).trim()).join(","));
  }
  const qs = params.toString();
  const res = await axios.get(`${baseUrl}${qs ? `?${qs}` : ""}`, {
    headers: { ...crmAuthHeaders() },
  });
  return res.data;
}

/**
 * Load one active custom script by UUID or script_key (no source_code in response).
 * @param {string} identifier
 */
export async function getCustomScriptByIdOrKey(identifier) {
  const id = String(identifier ?? '').trim();
  if (!id) {
    return { success: false, message: 'Script id or key is required.' };
  }
  const res = await axios.get(`${baseUrl}/${encodeURIComponent(id)}`, {
    headers: { ...crmAuthHeaders() },
  });
  const body = res.data;
  if (!body || typeof body !== 'object') {
    return { success: false, message: 'Invalid response.' };
  }
  // API shape: { success, data: scriptRow }. Some proxies double-wrap `data`.
  let row = body.data;
  const looksLikeScript = (o) =>
    o &&
    typeof o === 'object' &&
    !Array.isArray(o) &&
    (o.script_key != null || o.id != null);
  if (!looksLikeScript(row) && row?.success === true && looksLikeScript(row?.data)) {
    row = row.data;
  }
  return { ...body, data: row };
}

/**
 * Run VM script by script_key or customScriptId (dynamic form submit, etc.).
 * @param {{ scriptKey?: string, customScriptId?: string, recordId: string, objectName: string, inputData?: object }} params
 */
export async function runCustomScriptPayload({
  scriptKey,
  customScriptId,
  recordId,
  objectName,
  inputData,
}) {
  const body = {
    recordId,
    objectName,
    inputData: inputData && typeof inputData === 'object' && !Array.isArray(inputData) ? inputData : {},
  };
  if (scriptKey != null && String(scriptKey).trim() !== '') {
    body.scriptKey = String(scriptKey).trim();
  }
  if (customScriptId != null && String(customScriptId).trim() !== '') {
    body.customScriptId = String(customScriptId).trim();
  }
  const res = await axios.post(`${baseUrl}/run`, body, {
    headers: { ...crmAuthHeaders(), 'Content-Type': 'application/json' },
  });
  return res.data;
}

/**
 * Admin: list all custom_script rows (Custom Script Studio).
 */
export async function listCustomScriptsStudio() {
  const res = await axios.get(`${baseUrl}/editor`, {
    headers: { ...crmAuthHeaders() },
  });
  return res.data;
}

/**
 * Admin: load one script with source_code for editing.
 * @param {string} id - UUID
 */
export async function getCustomScriptStudioById(id) {
  const raw = String(id ?? '').trim();
  if (!raw) {
    return { success: false, message: 'Script id is required.' };
  }
  const res = await axios.get(`${baseUrl}/editor/${encodeURIComponent(raw)}`, {
    headers: { ...crmAuthHeaders() },
  });
  return res.data;
}

/**
 * Admin: create script row.
 * @param {object} payload — script_key, script_type, source_code, etc.; config/ui_code as objects or JSON strings
 */
export async function createCustomScriptStudio(payload) {
  const res = await axios.post(`${baseUrl}/editor`, payload, {
    headers: { ...crmAuthHeaders(), 'Content-Type': 'application/json' },
  });
  return res.data;
}

/**
 * Admin: update script row.
 */
export async function updateCustomScriptStudio(id, payload) {
  const raw = String(id ?? '').trim();
  if (!raw) {
    return { success: false, message: 'Script id is required.' };
  }
  const res = await axios.put(`${baseUrl}/editor/${encodeURIComponent(raw)}`, payload, {
    headers: { ...crmAuthHeaders(), 'Content-Type': 'application/json' },
  });
  return res.data;
}

/**
 * Admin: dry-run unsaved source in the sandbox VM.
 * @param {object} body — sourceCode, objectName, previewMode ('api'|'validation'), recordId?, inputData?, validationRecord?, …
 */
export async function previewCustomScript(body) {
  const res = await axios.post(`${baseUrl}/preview`, body, {
    headers: { ...crmAuthHeaders(), 'Content-Type': 'application/json' },
  });
  return res.data;
}
